import { Skills, STUCK_DELAY, Themes } from './data/constants';
import { randomFruitIndex, SPRITE_EXTENT } from './data/fruits';
import { getFruitSprite } from './fruitSprites';
import type { IPhysics } from './IPhysics';
import options from './options';
import type { ColorTheme } from './types/ColorTheme';
import type { VectorLike } from './types/VectorLike';
import { transformGuard } from './utils/transformGuard';
import { Vector } from './utils/Vector';

export class Marble {
  type = 'marble' as const;
  name: string = '';
  size: number = 0.5;
  color: string = 'red';
  hue: number = 0;
  impact: number = 0;
  weight: number = 1;
  skill: Skills = Skills.None;
  isActive: boolean = false;
  /** 바구니 모드에서 담긴 바구니 index. 아직 담기지 않았으면 null */
  basket: number | null = null;
  /** 겉모습으로 쓸 열매(FRUITS 의 index). 구슬마다 무작위로 정해진다 */
  fruit: number = 0;

  private _skillRate = 0.0005;
  private _coolTime = 5000;
  private _maxCoolTime = 5000;
  private _stuckTime = 0;
  private lastPosition: VectorLike = { x: 0, y: 0 };
  private theme: ColorTheme = Themes.dark;

  private physics: IPhysics;

  id: number;

  get position() {
    return this.physics.getMarblePosition(this.id) || { x: 0, y: 0, angle: 0 };
  }

  get x() {
    return this.position.x;
  }

  set x(v: number) {
    this.position.x = v;
  }

  get y() {
    return this.position.y;
  }

  set y(v: number) {
    this.position.y = v;
  }

  get angle() {
    return this.position.angle;
  }

  constructor(physics: IPhysics, order: number, max: number, name?: string, weight: number = 1) {
    this.name = name || `M${order}`;
    this.weight = weight;
    this.physics = physics;

    this._maxCoolTime = 1000 + (1 - this.weight) * 4000;
    this._coolTime = this._maxCoolTime * Math.random();
    this._skillRate = 0.2 * this.weight;

    const maxLine = Math.ceil(max / 10);
    const line = Math.floor(order / 10);
    const lineDelta = -Math.max(0, Math.ceil(maxLine - 5));
    // hue 는 겉모습이 열매로 바뀐 뒤에도 그대로 쓰인다. 순위 목록, 팀 명단, 미니맵 점,
    // 당첨자 표시가 모두 이 값으로 참가자를 구분하므로 열매 색으로 덮어쓰지 않는다
    this.hue = (360 / max) * order;
    this.color = `hsl(${this.hue} 100% 70%)`;
    this.fruit = randomFruitIndex();
    this.id = order;

    physics.createMarble(order, 10.25 + (order % 10) * 0.6, maxLine - line + lineDelta);
  }

  /**
   * @param deltaTime 벽시계 기준 경과(ms). 스킬 쿨타임 등 연출 시간에 쓴다
   * @param timeScale 이 틱에서 물리가 실제로 진행된 비율(슬로모션이면 1 미만).
   *   정지 판정은 이동 거리로 하므로 물리 시간에 맞춰 문턱을 줄여야 한다. 안 그러면 슬로모션 중
   *   천천히 구르는 구슬이 멈춘 것으로 오판되어 골인 직전에 랜덤으로 튕겨진다
   */
  update(deltaTime: number, timeScale: number = 1) {
    const stuckThreshold = 0.00001 * timeScale * timeScale;
    if (this.isActive && Vector.lenSq(Vector.sub(this.lastPosition, this.position)) < stuckThreshold) {
      // 누적에는 timeScale 을 곱하지 않는다. 이건 화면이 굳는 걸 막는 워치독이라 관객 체감 시간이 기준이다.
      // 물리 시간으로 바꾸면 슬로모션이 깊을수록 발동이 늦어지는데, 타겟 구슬이 멈추면 _goalDist 가 고정되어
      // 슬로모션이 풀리지 않으므로 교착을 깨야 할 상황에서 워치독이 오히려 무뎌진다
      this._stuckTime += deltaTime;

      if (this._stuckTime > STUCK_DELAY) {
        this.physics.shakeMarble(this.id);
        this._stuckTime = 0;
      }
    } else {
      this._stuckTime = 0;
    }
    this.lastPosition = { x: this.position.x, y: this.position.y };

    this.skill = Skills.None;
    if (this.impact) {
      this.impact = Math.max(0, this.impact - deltaTime);
    }
    if (!this.isActive) return;
    if (options.useSkills) {
      this._updateSkillInformation(deltaTime);
    }
  }

  private _updateSkillInformation(deltaTime: number) {
    if (this._coolTime > 0) {
      this._coolTime -= deltaTime;
    }

    if (this._coolTime <= 0) {
      this.skill = Math.random() < this._skillRate ? Skills.Impact : Skills.None;
      this._coolTime = this._maxCoolTime;
    }
  }

  render(
    ctx: CanvasRenderingContext2D,
    zoom: number,
    outline: boolean,
    isMinimap: boolean = false,
    skin: CanvasImageSource | undefined,
    viewPort: { x: number; y: number; w: number; h: number; zoom: number },
    theme: ColorTheme
  ) {
    this.theme = theme;
    const viewPortHw = viewPort.w / viewPort.zoom / 2;
    const viewPortHh = viewPort.h / viewPort.zoom / 2;
    const viewPortLeft = viewPort.x - viewPortHw;
    const viewPortRight = viewPort.x + viewPortHw;
    const viewPortTop = viewPort.y - viewPortHh;
    const viewPortBottom = viewPort.y + viewPortHh;
    const halfSize = this.size / 2;
    const isOutsideView =
      this.x + halfSize < viewPortLeft ||
      this.x - halfSize > viewPortRight ||
      this.y + halfSize < viewPortTop ||
      this.y - halfSize > viewPortBottom;
    if (!isMinimap && isOutsideView) {
      return;
    }
    const transform = ctx.getTransform();
    if (isMinimap) {
      this._renderMinimap(ctx);
    } else {
      this._renderNormal(ctx, zoom, outline, skin);
    }
    ctx.setTransform(transform);
  }

  private _renderMinimap(ctx: CanvasRenderingContext2D) {
    ctx.fillStyle = this.color;
    this._drawMarbleBody(ctx, true);
  }

  private _drawMarbleBody(ctx: CanvasRenderingContext2D, isMinimap: boolean) {
    ctx.beginPath();
    ctx.arc(this.x, this.y, isMinimap ? this.size : this.size / 2, 0, Math.PI * 2);
    ctx.fill();
  }

  private _renderNormal(ctx: CanvasRenderingContext2D, zoom: number, outline: boolean, skin?: CanvasImageSource) {
    const hs = this.size / 2;

    // 이름에 걸린 전용 스킨(캐릭터 이미지)이 있으면 그걸 우선한다. 없으면 열매를 쓴다
    if (skin) {
      transformGuard(ctx, () => {
        ctx.translate(this.x, this.y);
        ctx.rotate(this.angle);
        ctx.drawImage(skin, -hs, -hs, hs * 2, hs * 2);
      });
    } else {
      this._drawFruit(ctx, hs);
    }

    this._drawImpactFlash(ctx, hs);

    ctx.shadowColor = '';
    ctx.shadowBlur = 0;
    this._drawName(ctx, zoom);

    if (outline) {
      this._drawOutline(ctx, 2 / zoom);
    }
  }

  /**
   * 열매 스프라이트는 본체 반지름 1 을 SPRITE_EXTENT 만큼의 여유와 함께 담고 있다.
   * 잎과 꼭지가 본체 밖으로 나오므로 그 배수만큼 크게 그려야 본체 지름이 예전 원과,
   * 그리고 물리 반지름(0.25)과 맞는다
   */
  private _drawFruit(ctx: CanvasRenderingContext2D, halfSize: number) {
    const half = halfSize * SPRITE_EXTENT;
    transformGuard(ctx, () => {
      ctx.translate(this.x, this.y);
      ctx.rotate(this.angle);
      ctx.drawImage(getFruitSprite(this.fruit), -half, -half, half * 2, half * 2);
    });
  }

  /**
   * 예전에는 충격을 받으면 몸통 색의 명도를 올렸다. 이제 몸통이 스프라이트라 색을 못 바꾸므로
   * 흰 원을 겹쳐 같은 번쩍임을 낸다
   */
  private _drawImpactFlash(ctx: CanvasRenderingContext2D, halfSize: number) {
    const strength = Math.min(1, this.impact / 500);
    if (strength <= 0) return;
    ctx.save();
    ctx.globalAlpha = strength * 0.55;
    ctx.fillStyle = 'white';
    ctx.beginPath();
    ctx.arc(this.x, this.y, halfSize, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  /**
   * 이름표는 구슬 바로 아래에 붙는다(textBaseline 이 'top' 이고 y + 0.25 가 구슬 아래 끝).
   * textAlign 을 건드리므로 transformGuard 가 아니라 save/restore 로 감싼다. transformGuard 는
   * 변환만 되돌려서 정렬이 다음 렌더러로 새어나간다
   */
  private _drawName(ctx: CanvasRenderingContext2D, zoom: number) {
    ctx.save();
    ctx.font = `bold 12pt sans-serif`;
    // 번호를 구슬 정중앙 아래에 오게 한다. 예전 기본값('left')은 이름이 오른쪽으로 치우쳤다
    ctx.textAlign = 'center';
    ctx.strokeStyle = 'black';
    ctx.lineWidth = 3;
    // 참가자 색(this.color)으로 칠하면 같은 색 열매 위에서 묻힌다. 번호를 읽는 것이
    // 우선이라 흰색 + 검은 테두리로 고정한다
    ctx.fillStyle = 'white';
    ctx.shadowBlur = 0;
    ctx.translate(this.x, this.y + 0.25);
    ctx.scale(1 / zoom, 1 / zoom);
    ctx.strokeText(this.name, 0, 0);
    ctx.fillText(this.name, 0, 0);
    ctx.restore();
  }

  private _drawOutline(ctx: CanvasRenderingContext2D, lineWidth: number) {
    ctx.beginPath();
    ctx.strokeStyle = this.theme.marbleWinningBorder;
    ctx.lineWidth = lineWidth;
    ctx.arc(this.x, this.y, this.size / 2, 0, Math.PI * 2);
    ctx.stroke();
  }
}
